import { doc, getDoc, serverTimestamp, writeBatch, Timestamp } from 'firebase/firestore';
import { createUserWithEmailAndPassword, getAuth, signInWithEmailAndPassword } from 'firebase/auth';
import { db } from '../../firebase/firebase';
import { IOrganisation, IOrganisationRegistration, IMember } from '../../types/organisation';
import { v4 as uuidv4 } from 'uuid';
import { encryptEmail } from '../../utilities/emailEncryption';

/**
 * Register a new organisation with admin user
 */
export async function registerOrganisation(
  data: IOrganisationRegistration & { existingUser?: boolean }
) {
  try {
    const auth = getAuth();
    let user;

    if (data.existingUser) {
      // Sign in existing user
      const userCredential = await signInWithEmailAndPassword(
        auth,
        data.adminEmail,
        data.adminPassword
      );
      user = userCredential.user;
    } else {
      // Create new user
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        data.adminEmail,
        data.adminPassword
      );
      user = userCredential.user;
    }

    // Create the Organisation document
    const orgRef = doc(db, 'Organisations', uuidv4());
    const orgData: IOrganisation = {
      id: orgRef.id,
      name: data.name,
      type: data.type,
      description: data.description || '',
      adminId: user.uid,
      createdAt: serverTimestamp() as Timestamp,
      updatedAt: serverTimestamp() as Timestamp,
      isActive: true,
      settings: {
        allowAnonymousMembers: false,
        requireApproval: true,
      }
    };

    // Encrypt the admin email before storing
    const encryptedAdminEmail = await encryptEmail(data.adminEmail.trim().toLowerCase());
    
    // Create the member document for the admin
    const memberData: IMember = {
      userKey: user.uid,
      organisationId: orgRef.id,
      roles: ['organisationAdmin'],
      email: encryptedAdminEmail,
      name: user.displayName || data.adminEmail.split('@')[0],
      joinedAt: serverTimestamp() as Timestamp,
      groupIds: []
    };

    // Write organisation + admin membership in one batch so security rules can
    // verify adminId via getAfter while allowing the organisationAdmin claim.
    const userRef = doc(db, 'Users', user.uid);
    const userDoc = await getDoc(userRef);
    const batch = writeBatch(db);

    batch.set(orgRef, orgData);

    if (userDoc.exists()) {
      const existingData = userDoc.data();
      batch.update(userRef, {
        Organisations: {
          ...existingData.Organisations,
          [orgRef.id]: memberData
        }
      });
    } else {
      batch.set(userRef, {
        userKey: user.uid,
        email: data.adminEmail,
        displayName: user.displayName || data.adminEmail.split('@')[0],
        Organisations: {
          [orgRef.id]: memberData
        }
      });
    }

    await batch.commit();

    return { user: { userId: user.uid, email: data.adminEmail }, organisation: orgData };
  } catch (error) {
    console.error('Error registering Organisation:', error);
    throw error;
  }
}
